import React from "react";

import { faCreditCard } from "@fortawesome/sharp-regular-svg-icons/faCreditCard";
import { faBolt } from "@fortawesome/sharp-solid-svg-icons/faBolt";
import { faClock } from "@fortawesome/sharp-solid-svg-icons/faClock";

import { Box } from "~components/Box";
import { IconFontAwesome } from "~components/IconFontAwesome";
import { StoryblokEditable } from "~components/StoryblokEditable";

import { BankLogos } from "~animations/__layers__/mock_ui_components/BankLogos/BankLogos";

import { PayToBankSVG } from "./PayToBankSVG";
import * as styles from "./styles.css";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import type { BoxProps } from "~components/Box";
import type { StoryblokBlok } from "~types/storyblok.types";

const ICON_MAP = {
  faBolt,
  faClock,
} as const;

type IconMapKey = keyof typeof ICON_MAP;

export interface AnimLayerWithdrawalMethodProps
  extends StoryblokBlok,
    BoxProps {
  title?: string;
  description?: string;
  labelIcon?: IconMapKey;
  labelText?: string;
  variant: "card" | "payToBank";
}

export function AnimLayerWithdrawalMethod({
  labelIcon,
  labelText,
  title,
  description,
  variant = "card",
  ...rest
}: AnimLayerWithdrawalMethodProps) {
  const icon = labelIcon && (ICON_MAP[labelIcon] as IconDefinition);

  if (variant === "card") {
    return (
      <StoryblokEditable {...rest}>
        <Box
          paddingX="phoneSpacing1.5"
          paddingY="phoneSpacing2"
          className={styles.cardBorder}
          borderRadius="md"
        >
          <Box
            display="flex"
            alignItems="center"
            textAppearance="phone_text_xs"
            marginBottom="phoneSpacing1"
            className={styles.cardLabel}
          >
            <IconFontAwesome
              icon={icon as IconDefinition}
              size="xs"
              marginRight="phoneSpacing0"
            />
            {labelText}
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            marginBottom="phoneSpacing1"
          >
            <Box textAppearance="phone_text_sm" fontWeight="medium">
              {title}
            </Box>

            <IconFontAwesome
              icon={faCreditCard as IconDefinition}
              size="xs"
              className={styles.creditCardIcon}
            />
          </Box>
          <Box textAppearance="phone_text_xs">{description}</Box>
        </Box>
      </StoryblokEditable>
    );
  }

  if (variant === "payToBank") {
    return (
      <StoryblokEditable {...rest}>
        <Box
          paddingX="phoneSpacing1.5"
          paddingY="phoneSpacing2"
          className={styles.cardSelectedBorder}
          borderRadius="md"
        >
          <Box
            display="flex"
            alignItems="center"
            textAppearance="phone_text_xs"
            marginBottom="phoneSpacing1"
            className={styles.payToBankLabel}
          >
            <IconFontAwesome
              icon={icon as IconDefinition}
              size="xs"
              marginRight="phoneSpacing0"
            />
            {labelText}
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            marginBottom="phoneSpacing1"
          >
            <PayToBankSVG className={styles.payToBankLogo} />
            <BankLogos logoNumber={4} />
          </Box>
          <Box textAppearance="phone_text_xs">{description}</Box>
        </Box>
      </StoryblokEditable>
    );
  }

  return null;
}
